import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useProposal } from "../contexts/ProposalContext";
import Layout from "../components/layout/Layout";
import ProposalCard from "../components/proposals/ProposalCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";

const ProposalDevelopmentPage = () => {
  const navigate = useNavigate();
  const {
    proposals,
    loading: proposalsLoading,
    error: proposalsError,
    fetchProposals,
  } = useProposal();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  // Filter proposals based on search and filters
  const filteredProposals =
    proposals?.filter((proposal) => {
      const matchesSearch =
        !searchTerm ||
        proposal.proposal_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        proposal.customer_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        proposal.project_title
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" || proposal.status === statusFilter;

      const matchesType =
        typeFilter === "all" || proposal.proposal_type === typeFilter;

      return matchesSearch && matchesStatus && matchesType;
    }) || [];

  // Get unique values for filters
  const uniqueStatuses = [...new Set(proposals?.map((p) => p.status) || [])];
  const uniqueTypes = [
    ...new Set(proposals?.map((p) => p.proposal_type) || []),
  ];

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("Active");
    setTypeFilter("all");
  };

  const handleProposalClick = (proposalId) => {
    // Navigate to document builder for this proposal
    navigate(`/workspace/${proposalId}/documents`);
  };

  return (
    <Layout title="Proposal Development">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center">
                <FontAwesomeIcon
                  icon="file-alt"
                  className="mr-3 text-primary-500"
                />
                Proposal Development
              </h1>
              <p className="text-slate-600 mt-2">
                Select a proposal to create and edit documents
              </p>
            </div>

            {/* Stats */}
            <div className="hidden md:flex items-center space-x-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-slate-900">
                  {filteredProposals.length}
                </div>
                <div className="text-sm text-slate-600">Available</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">
                  {
                    filteredProposals.filter((p) => p.status === "Active")
                      .length
                  }
                </div>
                <div className="text-sm text-slate-600">Active</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <FontAwesomeIcon
                  icon="search"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  placeholder="Search proposals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                {uniqueStatuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                {uniqueTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {(searchTerm || statusFilter !== "Active" || typeFilter !== "all") && (
            <div className="mt-4">
              <button
                onClick={clearFilters}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <FontAwesomeIcon icon="times-circle" className="mr-2" />
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Error Message */}
        {proposalsError && <ErrorMessage message={proposalsError} />}

        {/* Loading State */}
        {proposalsLoading && (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="large" />
          </div>
        )}

        {/* Proposals Grid */}
        {!proposalsLoading && !proposalsError && (
          <>
            {filteredProposals.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                <FontAwesomeIcon
                  icon="inbox"
                  className="text-6xl text-slate-300 mb-4"
                />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">
                  No proposals found
                </h3>
                <p className="text-slate-500">
                  {searchTerm || statusFilter !== "Active" || typeFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Create a new proposal to get started"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProposals.map((proposal) => (
                  <ProposalCard
                    key={proposal.proposal_id}
                    proposal={proposal}
                    onClick={() => handleProposalClick(proposal.proposal_id)}
                    actionButton={{
                      label: "Create Documents",
                      icon: "file-alt",
                      variant: "primary",
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default ProposalDevelopmentPage;
